<template>
  <v-row v-if="specObject" class="ma-0 h-100">
    <split-view>
      <template #left>
        <v-list v-model:selected="tabs">
          <domain-file-list-item
            :item="{ name: '__template', path: '__template', displayName: 'Code Generation Steps' }"
            :domainObjects="domainObjects"
          />
        </v-list>
        <v-divider />
        <v-list
          height="774"
          bg-color="transparent"
          density="compact"
          open-strategy="multiple"
          v-model:selected="tabs"
          v-model:opened="opened"
        >
          <domain-file-list-item
            v-for="item in flattenedItems"
            :key="item.path"
            :item="item"
            :domainObjects="domainObjects"
            :canGenerate="!!selectedTemplate"
            :readModelMergeInfo="item.path === 'domain-model/readmodel' ? readModelMergeInfo : null"
            @click="toggle(item)"
          />
        </v-list>
      </template>
      <template #right>
        <div
          v-if="openedTab === '__template'"
          class="d-flex flex-column h-100"
          style="position: relative;"
        >
          <div v-if="openApiSpec" class="d-flex align-center mb-2">
            <v-tooltip location="top" open-delay="500">
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  size="small"
                  style="border-radius: 0 !important;"
                  variant="text"
                  density="compact"
                  class="text-none"
                  @click="openApiSpec = !openApiSpec"
                >
                  <v-icon class="mr-2">mdi-arrow-left</v-icon>
                  Back To Code Generation Steps
                </v-btn>
              </template>
              <span>View API Specification</span>
            </v-tooltip>
          </div>
          <div v-if="openApiSpec">
            <code-diff-viewer
              v-if="specObject.draftContents"
              :original="specObject.contents"
              :modelValue="specObject.draftContents"
              :isDirty="false"
              :readOnly="true"
              @save="emit('saveFile', specObject)"
              language="yaml"
            />
            <code-editor
              v-else
              :modelValue="specObject.contents"
              :isDirty="false"
              :readOnly="true"
              @save="emit('saveFile', specObject)"
              language="yaml"
            />
          </div>
          <div v-else class="d-flex flex-column pa-4 h-100" style="max-width: 800px;">
            <div class="d-flex align-center ga-2 mb-2">
              <v-icon v-if="!!selectedTemplate" color="green">mdi-checkbox-marked-outline</v-icon>
              <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
              <p class="text-h6">
                1. Choose a framework boilerplate and get coding in seconds.
              </p>
            </div>

            <div class="mb-2">
              <v-menu
                v-model="menuOpen"
                :close-on-content-click="false"
                location="bottom start"
                offset="5"
              >
                <template #activator="{ props }">
                  <v-list
                    v-bind="props"
                    elevation="2"
                    rounded
                    class="template-activator-list"
                  >
                    <v-list-item
                      v-if="selectedTemplate"
                      :key="selectedTemplate.name"
                      density="comfortable"
                      class="template-activator-item"
                    >
                      <template #prepend>
                        <v-avatar :color="selectedTemplate.color" size="36">
                          <v-icon :icon="selectedTemplate.icon" color="white" />
                        </v-avatar>
                      </template>

                      <v-list-item-title class="font-weight-medium">
                        {{ selectedTemplate.name }}
                        {{ selectedTemplate.isLegacy && selectedRepo?.name ? `- ${selectedRepo.name}` : '' }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="mb-1">
                        {{ selectedTemplate.description }}
                        {{ selectedTemplate.isLegacy && selectedRepo?.language ? `Language - ${selectedRepo.language}.` : '' }}
                      </v-list-item-subtitle>

                      <template #append>
                        <v-icon>mdi-chevron-down</v-icon>
                      </template>
                    </v-list-item>

                    <v-list-item
                      v-else
                      density="comfortable"
                      class="template-activator-placeholder"
                    >
                      <v-list-item-title class="text-grey-darken-1">
                        Select a framework...
                      </v-list-item-title>
                      <template #append>
                        <v-icon>mdi-chevron-down</v-icon>
                      </template>
                    </v-list-item>
                  </v-list>
                </template>

                <v-list
                  elevation="2"
                  rounded
                  class="bg-grey-lighten-4"
                  style="max-height: 420px;"
                >
                  <v-list-item
                    v-for="(template, index) in templates"
                    :key="index"
                    @click="selectTemplate(template)"
                    density="comfortable"
                    :disabled="template.disabled"
                    :class="[
                      'template-item',
                      template.name === selectedTemplate?.name
                        ? 'selected'
                        : 'hoverable'
                    ]"
                  >
                    <template #prepend>
                      <v-avatar :color="template.color" size="36">
                        <v-icon :icon="template.icon" color="white" />
                      </v-avatar>
                    </template>

                    <v-list-item-title class="font-weight-medium">
                      {{ template.name }}
                      {{ template.isLegacy && selectedRepo?.name ? `- ${selectedRepo.name}` : '' }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="mb-1">
                      {{ template.description }}
                      {{ template.isLegacy && selectedRepo?.language ? `Language - ${selectedRepo.language}.` : '' }}
                    </v-list-item-subtitle>
                    <template #append>
                      <v-btn
                        v-if="!template.disabled && template.repo"
                        :href="template.repo"
                        target="_blank"
                        rel="noopener"
                        icon
                        variant="text"
                        color="white"
                        @click.stop
                      >
                        <v-icon :color="template.name === selectedTemplate?.name ? 'white' : 'blue-grey'">
                          mdi-open-in-new
                        </v-icon>
                      </v-btn>
                      <span v-else-if="!template.isLegacy">
                        Coming Soon...
                      </span>
                    </template>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>

            <v-divider class="my-4" />
            <div class="d-flex align-center ga-2">
              <v-icon v-if="reviewedApiSpec" color="green">mdi-checkbox-marked-outline</v-icon>
              <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
              <p class="text-h6">
                2. Review the
              </p>
              <v-tooltip
                location="top"
                :text="hasRootEntity
                  ? 'To complete this step, click to inspect the OpenAPI / Swagger specification.'
                  : 'In order to view the API specification, connect at least one entity to the bounded context.'
                "
              >
                <template v-slot:activator="{ props }">
                  <div v-bind="props">
                    <v-btn
                      v-bind="props"
                      variant="text"
                      density="compact"
                      class="api-spec-btn"
                      :active="openApiSpec"
                      @click="onApiSpecClick"
                      :disabled="!hasRootEntity"
                    >
                      <span v-if="specObject.draftContents" class="api-change-indicator" />
                      API Specification
                    </v-btn>
                  </div>
                </template>
              </v-tooltip>
            </div>

            <v-divider class="my-4" />
            <div class="d-flex align-center ga-2">
              <v-icon v-if="codeGenerated" color="green">
                mdi-checkbox-marked-outline
              </v-icon>
              <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
              <div class="d-flex align-center ga-2">
                <p class="text-h6">
                  3. Generate code with AI
                </p>
                <div class="d-flex align-center">
                  <v-tooltip
                    location="top"
                    text="To complete this step, click to generate a backend with commands, entities, tests, queries, and/or a frontend application."
                  >
                    <template v-slot:activator="{ props }">
                      <div v-bind="props">
                        <v-btn
                          @click="handleGenerateClick"
                          class="generate-api-btn"
                          :disabled="store.state.isWorkflowDisabled || !selectedTemplate || (!includeBackend && !includeFrontend)"
                        >
                          <v-icon v-if="!isGenerating" start>mdi-brain</v-icon>
                          <v-icon v-else start>mdi-stop</v-icon>
                          {{ !specObject.draftContents ? allFilesApproved ? 'Regenerate API' : 'Generate API' : 'Update API' }}
                        </v-btn>
                      </div>
                    </template>
                  </v-tooltip>

                  <v-tooltip
                    location="top"
                    text="Generate a backend app in the server folder"
                  >
                    <template v-slot:activator="{ props }">
                      <div v-bind="props">
                        <v-checkbox
                          v-if="!selectedTemplate?.isLegacy"
                          v-model="includeBackend"
                          label="Include Backend"
                          density="compact"
                          color="green"
                          hide-details
                          class="ml-4"
                          :disabled="store.state.isWorkflowDisabled || !selectedTemplate"
                        />
                      </div>
                    </template>
                  </v-tooltip>

                  <v-tooltip
                    location="top"
                    text="Generate a single-page app in the client folder"
                  >
                    <template v-slot:activator="{ props }">
                      <div v-bind="props">
                        <v-checkbox
                          v-if="!selectedTemplate?.isLegacy"
                          v-model="includeFrontend"
                          label="Include Frontend"
                          density="compact"
                          color="green"
                          hide-details
                          class="ml-4"
                          :disabled="store.state.isWorkflowDisabled || !selectedTemplate"
                        />
                      </div>
                    </template>
                  </v-tooltip>
                </div>
              </div>
            </div>

            <v-divider class="my-4" />
            <div class="d-flex align-center ga-2">
              <v-icon v-if="isApproved" color="green">
                mdi-checkbox-marked-outline
              </v-icon>
              <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
              <div class="d-flex align-center ga-2">
                <p class="text-h6 pr-3">
                  4. Save changes
                </p>
                <v-tooltip
                  location="top"
                  max-width="640"
                  text="To complete this step, click here to approve all pending changes. Alternatively,
                   approve individual files or folders in the file tree (Domain tab or Code tab) by selecting
                   ‘Apply patch’ wherever yellow dots indicate changes."
                >
                  <template v-slot:activator="{ props }">
                    <div v-bind="props">
                      <v-btn :disabled="allFilesApproved" @click="approveAllChanges">
                        <v-icon start>mdi-content-save-all</v-icon>
                        <span>Approve all changes</span>
                      </v-btn>
                    </div>
                  </template>
                </v-tooltip>
                <v-divider vertical />
                <v-tooltip
                  location="top"
                  text="Discard all unapproved file changes (files with yellow dots in the file tree)."
                >
                  <template v-slot:activator="{ props }">
                    <div v-bind="props">
                      <v-btn :disabled="allFilesApproved" @click="discardAllChanges">
                        <v-icon start>mdi-content-save-off</v-icon>
                        <span>Discard all changes</span>
                      </v-btn>
                    </div>
                  </template>
                </v-tooltip>
              </div>
            </div>

            <v-divider class="my-4" />
            <div class="d-flex align-center ga-2">
              <v-icon v-if="isCommittedAndPushed" color="green">
                mdi-checkbox-marked-outline
              </v-icon>
              <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
              <v-tooltip location="top">
                <template v-slot:activator="{ props: props }">
                  <p class="text-h6" v-bind="props">
                    5. Commit and push to GitHub
                  </p>
                </template>
                <span>Navigate to the GitHub tab to commit and push your changes.</span>
              </v-tooltip>
            </div>

            <v-spacer></v-spacer>
            <div class="mt-5">
              <div class="d-flex justify-space-between">
                <div class="toolbar-absolute-bottom d-flex align-center overflow-hidden pb-1">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props: props }">
                      <v-btn
                        v-bind="props"
                        @click="openConfirmResetProject = true"
                        class="generate-api-btn mr-4"
                        :disabled="store.state.isWorkflowDisabled || (!selectedTemplate && !boundedContext.githubRepo)"
                      >
                        <v-icon start>mdi-broom</v-icon>
                        Reset Code View
                      </v-btn>
                    </template>
                    <span>Unlink the GitHub repository and delete all generated code files in Qlerify (files on GitHub will not be affected).</span>
                  </v-tooltip>
                </div>
                <div>
                  <ai-version-selector
                    style="max-width: 232px;"
                    :modelValue="selectedAiModel"
                    @update:modelValue="(val) => emit('update:selectedAiModel', val)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <template v-else>
          <template v-for="(object, filename) in domainObjects" :key="filename">
            <div v-if="openedTab === filename" style="position: relative;">
              <workflow-aggregates-code-viewer
                :object="object"
                @saveFile="saveDomainCode"
                @deleteFile="onFileDelete(object)"
                @update:object="(obj) => handleObjectUpdate(obj, filename)"
              />
            </div>
          </template>
        </template>
      </template>
    </split-view>
    <v-dialog v-model="askBeforeUpdateFramework" max-width="400">
      <v-card title="Warning">
        <v-card-text>
          You already have an existing project. Changing the framework will <b>permanently delete</b> all current
          code files and replace them with the selected template.
          <br><br>
          Are you sure you want to continue?
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" @click="cancelFrameworkUpdate">Cancel</v-btn>
          <v-btn variant="flat" color="primary" size="large" @click="onUpdateFramework">Continue</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="openConfirmResetProject" max-width="450">
      <v-card title="Confirm Project Reset">
        <v-card-text class="py-4">
          <p class="mb-4">You are about to perform an irreversible action.</p>
          <p>
            This will <b>permanently delete</b> all generated code files within this bounded context and unlink the associated GitHub repository.
          </p>
          <br>
          <p>Are you sure you want to proceed?</p>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" variant="text" @click="openConfirmResetProject = false">
            Cancel
          </v-btn>
          <v-btn variant="flat" color="error" size="large" @click="handleConfirmResetProject">
            Reset Project
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <PreviewPromptDialog
      v-model="openPreviewPrompt"
      :content="previewPromptContent"
      :cardDescription="previewCardDescription"
      @update:modelValue="onTogglePreviewDialog"
    />
  </v-row>
</template>

<script setup>
import _ from 'lodash'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { computed, ref, watch, provide } from 'vue'
import SplitView from '@/components/SplitView.vue'
import CodeEditor from '@/components/CodeEditor.vue'
import DomainFileListItem from '@/components/DomainFileListItem.vue'
import CodeDiffViewer from '@/components/CodeDiffViewer.vue'
import AiVersionSelector from '@/components/AiVersionSelector.vue'
import PreviewPromptDialog from '@/components/PreviewPromptDialog.vue'
import WorkflowAggregatesCodeViewer from '@/components/WorkflowAggregatesCodeViewer.vue'
import { collapsePaths, groupFilesByTags, flattenTree } from '@/codegen/utils'
import { sanitizeCardDescription } from '@/utils/sanitize'
import {
  DOMAIN_MODEL_STRUCTURE,
  CODE_EDITOR_APPLY_PATCH,
  CODE_EDITOR_GENERATE_AGAIN,
  CODE_EDITOR_EDIT_PROMPT,
  CODE_EDITOR_COPY_PROMPT
} from '@/codegen/constants'

const {
  isGenerating,
  specObject,
  fileTree,
  fileObjects,
  selectedAiModel,
  templates,
  selectedTemplate,
  boundedContext,
  selectedRepo,
  isCommitted,
  getDomainItemFinalPrompt,
  updateApiByPrompt
} = defineProps({
  isGenerating: {
    type: Boolean,
    default: false
  },
  specObject: {
    type: Object
  },
  fileTree: {
    type: Object
  },
  fileObjects: {
    type: Object
  },
  selectedAiModel: {
    type: Number,
    default: 4
  },
  templates: {
    type: Array
  },
  selectedTemplate: {
    type: Object,
    default: null
  },
  boundedContext: {
    type: Object
  },
  selectedRepo: {
    type: Object
  },
  isCommitted: {
    type: Boolean,
    default: false
  },
  getDomainItemFinalPrompt: {
    type: Function
  },
  updateApiByPrompt: {
    type: Function
  }
})
const emit = defineEmits(
  [
    'updateFramework',
    'update:object',
    'saveFile',
    'deleteFile',
    'update:selectedAiModel',
    'updateApi',
    'clearApi',
    'updateUI',
    'updateApiByTag'
  ])

const tabs = ref(['__template'])
const opened = ref([
  'domain-model',
  'domain-model/entity',
  'domain-model/command',
  'domain-model/readmodel',
  'domain-model/gwt'
])
const openedTab = ref('__template')
const pendingTemplate = ref(null)
const askBeforeUpdateFramework = ref(false)
const openApiSpec = ref(false)
const domainTree = ref({})
const defaultTree = ref({})
const collapseDomainTree = ref({})
const domainObjects = ref({})
const dirtyFolders = ref(new Set())
const specReviewed = ref(false)
const menuOpen = ref(false)
const includeBackend = ref(true)
const includeFrontend = ref(false)
const openConfirmResetProject = ref(false)
const openPreviewPrompt = ref(false)
const previewPromptContent = ref('')
const previewCardDescription = ref('')

const route = useRoute()
const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)
const mapRequirementType = (t) => {
  if (!t) return undefined
  if (t.triggerType) return 'command'
  if (t.queryType) return 'readmodel'
  if (t.gwtType) return 'gwt'
  return undefined
}
const domainModelData = computed(() => {
  const mainEntities = workflow.value.dataModels?.filter((model) => model.boundedContext === boundedContext.id) || []
  const allEntityIds = new Set(workflow.value.dataModels?.map((e) => e.id))
  const contextEntityIds = new Set(mainEntities.map((e) => e.id))

  const contextEvents = (
    route.params.eventId
      ? workflow.value.eventsJson.filter((event) => event.id === route.params.eventId)
      : workflow.value.eventsJson
  ).filter((event) => {
    const entityCard = event.requirementsJson.find((rt) => rt.cardType.aggregateType)
    return entityCard && contextEntityIds.has(entityCard.entityId)
  })

  const allRequirementCards = _.flatMap(contextEvents, (event) =>
    event.requirementsJson.map((card) => ({
      id: card.id,
      description: card.description,
      cardType: mapRequirementType(card.cardType),
      entityId: card.entityId,
      cardinality: card.cardinality
    }))
  )

  const [readModelCards, otherCards] = _.partition(allRequirementCards, (card) => card.cardType === 'readmodel')
  const readModelsWithEntity = readModelCards.filter((rm) => rm.entityId && allEntityIds.has(rm.entityId))

  const groupedReadModels = _.groupBy(
    readModelsWithEntity,
    (rm) => `${rm.entityId}-${rm.cardinality}`
  )

  const mergedReadModelCards = Object.values(groupedReadModels).map((group) => {
    const firstCard = group[0]
    const allIds = group.map((c) => c.id).sort()
    const entity = workflow.value.dataModels.find((e) => e.id === firstCard.entityId)
    return {
      id: firstCard.id,
      ids: allIds,
      description: firstCard.cardinality === 'one-to-many' ? `Get all ${entity.name}s` : `Get ${entity.name} by ID`,
      cardType: 'readmodel'
    }
  })

  const originalCount = readModelsWithEntity.length
  const mergedCount = mergedReadModelCards.length
  let mergeInfo = null

  if (originalCount > mergedCount) {
    mergeInfo = {
      original: originalCount,
      merged: mergedCount
    }
  }

  const relatedEntities = []
  for (const mainEntity of mainEntities) {
    for (const field of mainEntity.dataFields) {
      if (field.relatedEntityId) {
        const entity = workflow.value.dataModels.find(e => e.id === field.relatedEntityId)

        if (entity && !relatedEntities.some(e => e.id === entity.id)) {
          relatedEntities.push(entity)
        }
      }
    }
  }

  const availableEntities = [...mainEntities, ...relatedEntities]
    .map(model => ({
      id: model.id,
      description: model.name,
      cardType: 'entity'
    }))

  const finalCards = _.flatten([
    otherCards.map(({ id, description, cardType }) => ({ id, description, cardType })),
    mergedReadModelCards,
    availableEntities
  ])

  return {
    cards: finalCards,
    readModelMergeInfo: mergeInfo
  }
})
const eventCards = computed(() => domainModelData.value.cards)
const readModelMergeInfo = computed(() => domainModelData.value.readModelMergeInfo)

const flattenedItems = computed(() =>
  flattenTree(collapseDomainTree.value || {}, opened.value, '', 0)
)

const allFiles = computed(() => {
  return Object.values(fileObjects || {}).filter((file) =>
    file.filepath !== '__spec' && !file.filepath.includes('.qlerify/'))
})
const allFilesApproved = computed(() => {
  return allFiles.value.every((file) => !file.isDirty && !file.draftContents)
})
const reviewedApiSpec = computed(() => {
  return !!selectedTemplate && (specReviewed.value || !specObject.draftContents)
})
const codeGenerated = computed(() => {
  return !specObject.draftContents && !!allFiles.value.length
})
const isApproved = computed(() => {
  return reviewedApiSpec.value &&
    codeGenerated.value &&
    Object.keys(fileObjects).length > 1 &&
    !!allFilesApproved.value &&
    !specObject.draftContents
})
const isCommittedAndPushed = computed(() => {
  return reviewedApiSpec.value && codeGenerated.value && isApproved.value && isCommitted
})

const hasRootEntity = computed(() => {
  return !!boundedContext.systems[0].groupedBy.rootEntity
})

const selectTemplate = (template) => {
  if (selectedTemplate?.name === template.name) {
    return
  }

  const projectExists = !!allFiles.value.length

  if (projectExists) {
    askBeforeUpdateFramework.value = true
    pendingTemplate.value = template
  } else {
    emit('updateFramework', template)
  }

  menuOpen.value = false
}

const onUpdateFramework = () => {
  if (pendingTemplate.value) {
    emit('updateFramework', pendingTemplate.value)
  }
  askBeforeUpdateFramework.value = false
  pendingTemplate.value = null
}

const handleObjectUpdate = (object) => {
  const updatedObject = { ...fileObjects[object.realpath], isDirty: true, tags: object.tags }
  emit('update:object', updatedObject)
  updateDomain(fileObjects)
}

const cancelFrameworkUpdate = () => {
  askBeforeUpdateFramework.value = false
  pendingTemplate.value = null
}

const onApiSpecClick = () => {
  openApiSpec.value = true
  specReviewed.value = true
}

const handleGenerateClick = () => {
  emit('updateApi', includeBackend.value, includeFrontend.value)
}

const handleConfirmResetProject = () => {
  openConfirmResetProject.value = false
  emit('clearApi')
}

const collapseObject = (obj, maxDepth = 3, currentDepth = 0) => {
  if (obj === null || typeof obj !== 'object') {
    return obj
  }

  if (currentDepth >= maxDepth) {
    // Collapse deeper structures
    return collapsePaths(obj)
  }

  if (Array.isArray(obj)) {
    return obj.map(item => collapseObject(item, maxDepth, currentDepth + 1))
  }

  const collapsed = {}
  for (const [key, value] of Object.entries(obj)) {
    collapsed[key] = collapseObject(value, maxDepth, currentDepth + 1)
  }
  return collapsed
}

const updateDomain = (objects) => {
  const templates = {
    'domain-model/entity': 'root/.qlerify/entity-template.md',
    'domain-model/command': 'root/.qlerify/command-template.md',
    'domain-model/readmodel': 'root/.qlerify/readmodel-template.md',
    'domain-model/gwt': 'root/.qlerify/gwt-template.md'
  }
  const types = ['entity', 'command', 'readmodel', 'gwt']
  for (const type of types) {
    const domainItems = Object.keys(defaultTree.value['domain-model'][type])
    for (const item of domainItems) {
      templates[`domain-model/${type}/${item}`] = `root/.qlerify/${type}-templates/${type}-${item}.md`
    }
  }

  domainObjects.value = Object.fromEntries(
    Object.entries(templates).map(([key, path]) => {
      const { filepath, ...rest } = objects[path] ?? {}
      return [key, { ...rest, filepath, realpath: filepath }]
    })
  )
  dirtyFolders.value = new Set()

  const domainPathPerTag = {
    entity: 'domain-model/entity/',
    readmodel: 'domain-model/readmodel/',
    command: 'domain-model/command/',
    gwt: 'domain-model/gwt/'
  }
  const tempPaths = {
    'domain-model/entity/': {},
    'domain-model/readmodel/': {},
    'domain-model/command/': {},
    'domain-model/gwt/': {}
  }

  const validCardIds = new Set(eventCards.value.map(card => card.id))

  Object.entries(domainPathPerTag).forEach(([tag, basePath]) => {
    Object.entries(objects).forEach(([path, object]) => {
      const tags = object.tags || []
      const typeTags = tags.map(tag => {
        const [type, id] = tag.split(/-(.+)/)
        return { type, id }
      })
      let cardIds = []
      for (const { type, id } of typeTags) {
        if (type === tag) {
          cardIds.push(id)
        }
      }
      cardIds = cardIds.filter(cardId => validCardIds.has(cardId))
      if (cardIds?.length) {
        for (const cardId of cardIds) {
          const cardDescription = eventCards.value.find(card => card.id === cardId)?.description || ''
          // const fullPath = _.kebabCase(sanitizeCardDescription(cardDescription)) + '/' + object.filepath.replace('root/server/src/', '')
          const fullPath = sanitizeCardDescription(cardDescription).toLowerCase().replace(/\s+/g, '-') + '/' + object.filepath.replace('root/server/src/', '')
          domainObjects.value[basePath + fullPath] = { ...object, filepath: fullPath, realpath: object.filepath, cardDescription }

          if (object.draftContents) {
            const parts = (basePath + fullPath).split('/')
            let path = ''

            for (let i = 0; i < parts.length; i++) {
              path = i === 0 ? parts[i] : `${path}/${parts[i]}`
              dirtyFolders.value.add(path)
            }
          }

          const parts = fullPath.split('/')
          let curr = tempPaths[basePath]
          parts.forEach(part => {
            if (!curr[part]) {
              curr[part] = {}
            }
            curr = curr[part]
          })
        }
      }
    })
  })

  domainTree.value = _.merge({}, defaultTree.value, {
    'domain-model': {
      entity: tempPaths['domain-model/entity/'],
      readmodel: tempPaths['domain-model/readmodel/'],
      command: tempPaths['domain-model/command/'],
      gwt: tempPaths['domain-model/gwt/']
    }
  })

  collapseDomainTree.value = collapseObject(domainTree.value)
}

const toggle = (item) => {
  const index = opened.value.indexOf(item.path)
  if (index >= 0) {
    opened.value.splice(index, 1)
  } else {
    opened.value.push(item.path)
  }
}

provide('treeAction', async (data) => {
  const files = Object.entries(domainObjects.value)
    .filter(([key]) => {
      const parts = key.split('/')
      return parts.length > 3 && key.startsWith(data.path)
    })
    .map(([_, value]) => value)
  switch (data.type) {
    case CODE_EDITOR_APPLY_PATCH:
      for (const file of files) {
        const tempFile = _.cloneDeep(fileObjects[file.realpath])
        tempFile.contents = tempFile.draftContents
        tempFile.draftContents = null
        emit('saveFile', tempFile)
      }
      break
    case CODE_EDITOR_GENERATE_AGAIN:
      const pathParts = data.path.split('/')

      if (pathParts.length === 1) {
        emit('updateApi', true, false)
        return
      }

      if (pathParts.length === 2) {
        const domainItems = Object.entries(domainObjects.value)
          .filter(([key]) => {
            const parts = key.split('/')
            return parts.length === 3 && key.startsWith(data.path)
          })
          .map(([_, value]) => value)

        for (const item of domainItems) {
          const parts = item.filepath.split('/')
          const itemInfo = parts[parts.length - 1].replace('.md', '')
          const itemInfoParts = itemInfo.split('-')
          const type = itemInfoParts[0]
          const description = `${itemInfoParts.slice(1).join(' ')}`
          const loadingText = `Generating code for ${type} <b>${description}</b>`

          const userPrompt = getDomainItemFinalPrompt(item)
          try {
            await updateApiByPrompt({ userPrompt, loadingText })
          } catch (e) {
            // Do nothing
          }
        }

        return
      }

      if (!files.length) {
        const normalizedPath = data.path.split('/').slice(1, 3).join('-')
          .toLowerCase()
          .replace(/\s+/g, '-')
        const type = data.path.split('/')[1]
        const promptPath = `root/.qlerify/${type}-templates/${normalizedPath}.md`
        if (!fileObjects[promptPath]) {
          store.commit('snackbar/showMessage', {
            content: `Cannot find prompt template for - ${promptPath}`,
            timeout: 6000,
            color: 'red',
            location: 'center center'
          }, { root: true })
          return
        }

        const userPrompt = getDomainItemFinalPrompt(fileObjects[promptPath])
        try {
          await updateApiByPrompt({ userPrompt })
        } catch (e) {
          // Do nothing
        }
      } else {
        const groups = groupFilesByTags(files)
        for (const tag in groups) {
          emit('updateApiByTag', { tag, files: groups[tag] || [] })
        }
      }
      break
    case CODE_EDITOR_EDIT_PROMPT:
      openedTab.value = data.path
      break
    case CODE_EDITOR_COPY_PROMPT:
      const normalizedPath = data.path.split('/').slice(1, 3).join('-')
        .toLowerCase()
        .replace(/\s+/g, '-')
      const type = data.path.split('/')[1]

      const promptPath = `root/.qlerify/${type}-templates/${normalizedPath}.md`
      const prompt = getDomainItemFinalPrompt(fileObjects[promptPath])
      previewPromptContent.value = prompt
      openPreviewPrompt.value = true
      const desc = files[0]?.cardDescription
      previewCardDescription.value = desc ? `${_.capitalize(type)}: ${desc}` : ''
      break
  }
})

const onTogglePreviewDialog = (val) => {
  if (!val) {
    previewPromptContent.value = ''
    previewCardDescription.value = ''
  }
}

const approveAllChanges = () => {
  const fileredKeys = Object.keys(fileObjects)
    .filter(key => fileObjects[key]?.draftContents)

  for (const key of fileredKeys) {
    const tempFile = _.cloneDeep(fileObjects[key])
    tempFile.contents = tempFile.draftContents
    tempFile.draftContents = null
    emit('saveFile', tempFile)
  }
}

const discardAllChanges = () => {
  const fileredKeys = Object.keys(fileObjects)
    .filter(key => fileObjects[key]?.draftContents)

  for (const key of fileredKeys) {
    const tempFile = _.cloneDeep(fileObjects[key])

    if (tempFile.contents) {
      tempFile.draftContents = null
      emit('saveFile', tempFile)
    } else {
      emit('deleteFile', tempFile)
    }
  }
}

// const getAllPaths = (fileTree, prefix) => {
//   const result = []
//   Object.entries(fileTree).forEach(([path]) => {
//     if (Object.keys(fileTree[path]).length !== 0) {
//       result.push(prefix + '/' + path)
//       result.push(...getAllPaths(fileTree[path], prefix + '/' + path))
//     }
//   })
//   return result
// }

const saveDomainCode = (object) => {
  const transformedObject = _.cloneDeep(object)
  transformedObject.filepath = transformedObject.realpath
  delete transformedObject.realpath
  emit('saveFile', transformedObject)
}

const onFileDelete = (object) => {
  emit('deleteFile', object)
}

// const handleDomainAction = async (data) => {
//   const files = Object.entries(domainObjects.value)
//     .filter(([key, value]) => key.startsWith(data.domainPath))
//     .map(([key, value]) => value)
//   switch (data.type) {
//     case 'regenerate':
//       const groups = groupFilesByTags(files)
//       for (const tag in groups) {
//         emit('updateApiByTag', { tag, files: groups[tag] })
//       }
//       break
//     case 'applyPatch':
//       for (const file of files) {
//         if (file.contents !== file.draftContents) {
//           const tempFile = _.cloneDeep(file)
//           tempFile.contents = tempFile.draftContents
//           tempFile.draftContents = null
//           saveDomainCode(tempFile)
//         }
//       }
//       break
//     case 'editPrompt':
//       openedTab.value = data.domainPath.split('/').pop()
//       break
//     case 'copyPrompt':
//       const normalizedPath = _.kebabCase(data.domainPath.split('/').slice(1, 3).join('-'))
//       const promptPath = `root/.qlerify/${normalizedPath}.md`
//       await navigator.clipboard.writeText(fileObjects[promptPath]?.contents || '')
//       store.commit('snackbar/showMessage', { content: 'Copied prompt', timeout: 3000, color: 'blue' }, { root: true })
//       break
//   }
// }

watch(() => tabs.value, (newVal) => {
  if (newVal[0]) {
    openedTab.value = newVal[0]
  } else {
    tabs.value[0] = openedTab.value
  }
})

watch([() => fileObjects, () => fileTree], () => {
  updateDomain(fileObjects)
}, { deep: true })

watch(
  () => eventCards.value,
  (cards) => {
    const defaultDomainTree = _.cloneDeep(DOMAIN_MODEL_STRUCTURE)
    const domainCards = cards.filter(card => card.cardType)

    for (const card of domainCards) {
      const description = sanitizeCardDescription(card.description)
        .toLowerCase()
        .replace(/\s+/g, '-')

      defaultDomainTree['domain-model'][card.cardType][description] = {}
    }

    defaultTree.value = _.cloneDeep(defaultDomainTree)
    domainTree.value = _.cloneDeep(defaultDomainTree)

    updateDomain(fileObjects)
  },
  { immediate: true }
)

// watch(() => opened.value, (newOpened) => {
//   if (savedOpenedCount.value > newOpened.length) {
//     savedOpenedCount.value = newOpened.length
//   } else {
//     const lastOpened = newOpened.pop()
//     const parts = lastOpened.split('/')
//     let curTree = domainTree.value
//     parts.forEach((part) => {
//       curTree = curTree[part]
//     })
//     opened.value.push(...getAllPaths(curTree, lastOpened))
//     opened.value.push(lastOpened)
//     savedOpenedCount.value = opened.value.length
//   }
// })
</script>

<style scoped>
.api-change-indicator {
  position: absolute;
  top: 8px;
  right: 4px;
  width: 8px;
  height: 8px;
  background-color: #ff5252;
  border-radius: 50%;
  box-shadow: 0 0 0 rgba(255, 82, 82, 0.7);
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 82, 82, 0.7);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(255, 82, 82, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 82, 82, 0);
  }
}

.template-activator-list {
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border: 1px solid #E0E0E0;
}

.template-activator-list:hover {
  border-color: #BDBDBD;
  background-color: #fafafa;
}

.template-item {
  transition: background-color 0.2s, color 0.2s;
}

.template-item.hoverable {
  background-color: #f5f5f5 !important;
}

.template-item.hoverable:hover {
  background-color: #eeeeee !important;
  color: black;
}

.template-item.selected {
  background-color: #616161 !important;
  color: white;
}

.template-item.selected:hover {
  background-color: #546E7A !important;
  color: white;
}

.template-item {
  &.v-list-item--disabled {
    opacity: 0.4;
  }
}

.api-spec-btn {
  height: 36px !important;
  padding-left: 8px;
}
</style>
